import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { LoginContext } from '../../../config/authentication/LoginContext';
import { useContext, Dispatch, SetStateAction, DispatchWithoutAction } from 'react';
import { ERROR_OTP_HTTP_CODE } from '../../../rest/responseCodes';
import { LoginAction } from './useLoginStatus';
import { post } from '../../../rest/api';
import { URL_API_LOGIN_PATH1, URL_API_LOGIN_PATH2 } from '../../../constants/urls';
import { LoginResponse } from '../../../rest/LoginResponse';
import { UserData } from '../../../model/UserData';
import { LoginDialogCallback } from './LoginDialogState';
import { LoginRequestData } from '../../../rest/LoginRequestData';
import { LoginOTP } from '../../../rest/LoginOTP';

/**
 * @param notifyDefaulter - callback para notificar que
 *  el cliente se encuentra en estado de mora.
 */
export const useLoginRequest = (
  tokenInputVisible: boolean,
  dispatch: Dispatch<LoginAction>,
  setRequestErrorMessage: Dispatch<SetStateAction<string | undefined>>,
  getEmail: () => string | undefined,
  setAlreadyAcceptTycs: Dispatch<SetStateAction<boolean>>,
  otpInput: string | undefined,
  loginToken: string | undefined,
  setLoginToken: (tkn?: string, expDate?: string) => void,
  afterLogin?: LoginDialogCallback,
  notifyDefaulter?: DispatchWithoutAction
) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const loginData = useContext(LoginContext);

  /**Token de Google Recaptcha */
  const getRecaptchaToken = async () => {
    if (!executeRecaptcha) {
      return undefined;
    }
    return await executeRecaptcha();
  };

  async function getLoginData(): Promise<LoginRequestData> {
    return {
      email: getEmail(),
      googleToken: (await getRecaptchaToken()) ?? '',
    };
  }

  function getOTPData(): LoginOTP {
    return {
      OTP: otpInput ? Number.parseInt(otpInput) : undefined,
    };
  }

  function onError(reason: any, errorCode?: number) {
    if (tokenInputVisible && errorCode === ERROR_OTP_HTTP_CODE) dispatch(LoginAction.ERROR_TOKEN);
    else dispatch(LoginAction.ERROR);

    setRequestErrorMessage(reason);
  }

  function processResponse1(resp: LoginResponse) {
    if (!resp) {
      onError(undefined);
    } else if (resp.defaulter) {
      /**Cliente en estado de mora. */
      notifyDefaulter?.();
    } else if (resp.token) {
      setAlreadyAcceptTycs(resp.acceptTerms!);
      setLoginToken(resp.token, resp.expirationDate);
      setRequestErrorMessage(undefined);
      dispatch(LoginAction.CONTINUAR);
    }
  }

  function processResponse2(resp: UserData) {
    if (resp && resp.token && resp.expirationDate) {
      loginData?.saveUser?.({ ...resp, email: getEmail() });
      afterLogin?.(resp);
    } else {
      onError(undefined);
    }
  }

  async function request1() {
    post(URL_API_LOGIN_PATH1, await getLoginData(), processResponse1, onError);
  }

  async function request2() {
    const headers = loginToken ? { token: loginToken } : undefined;
    post(URL_API_LOGIN_PATH2, getOTPData(), processResponse2, onError, undefined, headers);
  }

  return [request1, request2] as const;
};
